import { create } from "zustand";

export type AuthStatus = "idle" | "loading" | "authenticated" | "error";

export interface AuthState {
  token: string | null;
  status: AuthStatus;
  errorMessage: string | null;
  authenticate: (initData?: string) => Promise<boolean>;
  setToken: (token: string | null) => void;
  logout: () => void;
}

export function getApiBaseUrl(): string {
  return import.meta.env.VITE_API_URL ?? "";
}

export const useAuthStore = create<AuthState>((set) => ({
  token: null,
  status: "idle",
  errorMessage: null,

  setToken: (token) => {
    set({
      token,
      status: token ? "authenticated" : "idle",
      errorMessage: null,
    });
  },

  logout: () => {
    set({
      token: null,
      status: "idle",
      errorMessage: null,
    });
  },

  authenticate: async (customInitData?: string) => {
    const initData = customInitData ?? window.Telegram?.WebApp?.initData;

    if (!initData) {
      if (import.meta.env.DEV && import.meta.env.MODE !== "test") {
        set({ status: "loading", errorMessage: null });
        try {
          const baseUrl = getApiBaseUrl();
          const response = await fetch(`${baseUrl}/api/auth/telegram/dev-mock`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          });

          if (response.ok) {
            const data = await response.json();
            if (data?.token && typeof data.token === "string") {
              set({
                token: data.token,
                status: "authenticated",
                errorMessage: null,
              });
              return true;
            }
          } else {
            let errorDetail = "";
            try {
              const errJson = await response.json();
              errorDetail = errJson.error || JSON.stringify(errJson);
            } catch {
              errorDetail = await response.text();
            }
            set({
              token: null,
              status: "error",
              errorMessage: `Dev Mock Auth Failed (${response.status}): ${errorDetail || "Unknown server response"}`,
            });
            return false;
          }
        } catch (error) {
          set({
            token: null,
            status: "error",
            errorMessage: `Cannot connect to API server (http://localhost:3001). Please make sure "npm run dev:api" is running. (${error instanceof Error ? error.message : String(error)})`,
          });
          return false;
        }
      }

      set({
        token: null,
        status: "error",
        errorMessage: "Не найдены данные авторизации Telegram. Пожалуйста, откройте приложение внутри Telegram.",
      });
      return false;
    }

    set({ status: "loading", errorMessage: null });

    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/api/auth/telegram`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ initData }),
      });

      if (!response.ok) {
        set({
          token: null,
          status: "error",
          errorMessage: "Не удалось войти в аккаунт. Попробуйте позже или обратитесь в поддержку.",
        });
        return false;
      }

      const data = await response.json();
      if (!data?.token || typeof data.token !== "string") {
        set({
          token: null,
          status: "error",
          errorMessage: "Некорректный ответ сервера. Попробуйте позже или обратитесь в поддержку.",
        });
        return false;
      }

      set({
        token: data.token,
        status: "authenticated",
        errorMessage: null,
      });
      return true;
    } catch {
      set({
        token: null,
        status: "error",
        errorMessage: "Ошибка сети при авторизации. Попробуйте позже или обратитесь в поддержку.",
      });
      return false;
    }
  },
}));
